import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';
import { DollarSign, Gift, Users, ShoppingBag, TrendingUp, Award, Star, Droplet, Fish, Wallet, Camera, Settings, Zap, Clock, ChevronDown, ChevronUp, Download, User, Bot, Info } from 'lucide-react';
import { PlanUpsellPopup } from '../monetization/PlanUpsellPopup';
// Sample data
const monthlyEarnings = [{
  name: 'Jan',
  creator: 1200,
  gifts: 800,
  subscriptions: 2400,
  shop: 400
}, {
  name: 'Feb',
  creator: 1300,
  gifts: 750,
  subscriptions: 2300,
  shop: 450
}, {
  name: 'Mar',
  creator: 1400,
  gifts: 900,
  subscriptions: 2500,
  shop: 500
}, {
  name: 'Apr',
  creator: 1500,
  gifts: 1000,
  subscriptions: 2600,
  shop: 550
}, {
  name: 'May',
  creator: 1600,
  gifts: 1100,
  subscriptions: 2700,
  shop: 600
}, {
  name: 'Jun',
  creator: 1700,
  gifts: 1200,
  subscriptions: 2800,
  shop: 650
}, {
  name: 'Jul',
  creator: 1800,
  gifts: 1300,
  subscriptions: 2900,
  shop: 700
}, {
  name: 'Aug',
  creator: 1900,
  gifts: 1400,
  subscriptions: 3000,
  shop: 750
}, {
  name: 'Sep',
  creator: 2000,
  gifts: 1500,
  subscriptions: 3100,
  shop: 800
}, {
  name: 'Oct',
  creator: 2100,
  gifts: 1600,
  subscriptions: 3200,
  shop: 850
}, {
  name: 'Nov',
  creator: 2200,
  gifts: 1700,
  subscriptions: 3300,
  shop: 900
}, {
  name: 'Dec',
  creator: 2300,
  gifts: 1800,
  subscriptions: 3400,
  shop: 950
}];
// Revenue split data
const revenueSplitData = [{
  name: 'AI Revenue',
  value: 5800
}, {
  name: 'Human Revenue',
  value: 2800
}];
// Commission tiers
const commissionTiers = [{
  tier: 'Free',
  fee: '15%',
  threshold: '$0'
}, {
  tier: 'Standard',
  fee: '10%',
  threshold: '$30'
}, {
  tier: 'Premium',
  fee: '8.5%',
  threshold: '$100'
}];
// Customer segments data
const customerSegments = [{
  id: 1,
  name: 'VIP (Whales)',
  count: 25,
  revenue: 4200,
  aiHandled: 0,
  humanHandled: 25,
  arpu: 168,
  icon: <Droplet size={16} data-id="element-4784" />,
  aiRevenue: 0,
  humanRevenue: 4200,
  label: 'VIP'
}, {
  id: 2,
  name: 'Spenders (Dolphins)',
  count: 120,
  revenue: 2400,
  aiHandled: 30,
  humanHandled: 90,
  arpu: 20,
  icon: <Fish size={16} data-id="element-4785" />,
  aiRevenue: 600,
  humanRevenue: 1800,
  label: 'Spender'
}, {
  id: 3,
  name: 'Low Spenders (Minnows)',
  count: 450,
  revenue: 1350,
  aiHandled: 405,
  humanHandled: 45,
  arpu: 3,
  icon: <Fish size={16} data-id="element-4786" />,
  aiRevenue: 1215,
  humanRevenue: 135,
  label: 'Low Spender'
}, {
  id: 4,
  name: 'Newbies (Non-Spenders)',
  count: 1200,
  revenue: 0,
  aiHandled: 1200,
  humanHandled: 0,
  arpu: 0,
  icon: <Wallet size={16} data-id="element-4787" />,
  aiRevenue: 0,
  humanRevenue: 0,
  label: 'Newbie'
}, {
  id: 5,
  name: 'Dormants (Churn Risks)',
  count: 350,
  revenue: 0,
  aiHandled: 350,
  humanHandled: 0,
  arpu: 0,
  icon: <Clock size={16} data-id="element-4788" />,
  aiRevenue: 0,
  humanRevenue: 0,
  label: 'Dormant'
}];
// Operator performance data
const operatorPerformance = [{
  id: 1,
  name: 'Sarah',
  revenue: 3200,
  customers: 65,
  commission: 320
}, {
  id: 2,
  name: 'Mike',
  revenue: 2800,
  customers: 58,
  commission: 280
}, {
  id: 3,
  name: 'Jessica',
  revenue: 2400,
  customers: 52,
  commission: 240
}, {
  id: 4,
  name: 'AI Assistant',
  revenue: 5800,
  customers: 2070,
  commission: 870
}];
// Campaign data
const campaignData = [{
  id: 1,
  name: 'Summer Swimsuit Challenge',
  type: 'Human-driven',
  earned: 450,
  status: 'Active Campaign'
}, {
  id: 2,
  name: 'Interactive Toy Promotion',
  type: 'AI-driven',
  earned: 320,
  status: 'Sponsored Content'
}, {
  id: 3,
  name: "Valentine's Special Effect",
  type: 'AI-driven',
  earned: 180,
  status: 'Effect House Revenue'
}, {
  id: 4,
  name: 'VIP Exclusive Experience',
  type: 'Human-driven',
  earned: 620,
  status: 'Premium Campaign'
}];
// Recent activity data
const recentActivity = [{
  id: 1,
  customer: 'CamWhale92',
  action: 'Upgraded to VIP',
  amount: 120,
  time: '2 hours ago',
  icon: <Camera size={16} data-id="element-4789" />
}, {
  id: 2,
  customer: 'BigSpender45',
  action: 'Purchased premium content',
  amount: 75,
  time: '5 hours ago',
  icon: <Wallet size={16} data-id="element-4790" />
}, {
  id: 3,
  customer: 'LonelyHeart84',
  action: 'Sent gifts',
  amount: 50,
  time: '1 day ago',
  icon: <Gift size={16} data-id="element-4791" />
}, {
  id: 4,
  customer: 'NewUser123',
  action: 'Auto-routed to AI',
  amount: 0,
  time: '2 days ago',
  icon: <Bot size={16} data-id="element-4792" />
}];
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300'];
const REVENUE_SPLIT_COLORS = ['#8884d8', '#82ca9d'];
export function MonetizationSection() {
  const [expandedSections, setExpandedSections] = useState({
    revenueSplit: true,
    customerRouting: true,
    customerSegments: true,
    commissionAutomation: true,
    operatorPerformance: true,
    campaignHub: true
  });
  const [showUpsellPopup, setShowUpsellPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // Check if this is the first login to show the upsell popup
  useEffect(() => {
    const hasSeenUpsell = localStorage.getItem('hasSeenUpsellPopup');
    if (!hasSeenUpsell) {
      setShowUpsellPopup(true);
    }
  }, []);
  const handleSelectPlan = (plan: string) => {
    setSelectedPlan(plan);
    setShowUpsellPopup(false);
    localStorage.setItem('hasSeenUpsellPopup', 'true');
    localStorage.setItem('selectedPlan', plan);
    // Show success notification
    const notificationEvent = new CustomEvent('showNotification', {
      detail: {
        title: 'Plan Selected',
        message: `You've successfully selected the ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan!`,
        type: 'success'
      }
    });
    document.dispatchEvent(notificationEvent);
  };
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Calculate AI commission
  const aiRevenue = revenueSplitData[0].value;
  const aiCommissionRate = 0.15; // 15% commission rate for AI
  const aiCommission = aiRevenue * aiCommissionRate;
  // Calculate human revenue and commission
  const humanRevenue = revenueSplitData[1].value;
  const totalRevenue = aiRevenue + humanRevenue;
  const aiPercentage = Math.round(aiRevenue / totalRevenue * 100);
  const humanPercentage = 100 - aiPercentage;
  // Calculate time saved by AI
  const totalCustomers = customerSegments.reduce((sum, segment) => sum + segment.count, 0);
  const aiHandledCustomers = customerSegments.reduce((sum, segment) => sum + segment.aiHandled, 0);
  const aiHandledPercentage = Math.round(aiHandledCustomers / totalCustomers * 100);
  // Calculate total AI and Human revenue
  const totalAiRevenue = customerSegments.reduce((sum, segment) => sum + segment.aiRevenue, 0);
  const totalHumanRevenue = customerSegments.reduce((sum, segment) => sum + segment.humanRevenue, 0);
  const handleExportData = () => {
    // This would normally trigger a download of CSV/PDF with the data
    // including AI vs Human revenue split and AI Commission Fee columns
    alert('Exporting data with AI vs Human revenue split and Commission Fee information');
  };
  return <div className="w-full space-y-6" data-id="element-4793">
      {/* Upsell Popup */}
      <PlanUpsellPopup isOpen={showUpsellPopup} onClose={() => setShowUpsellPopup(false)} onSelectPlan={handleSelectPlan} data-id="element-4794" />
      {/* Selected Plan Banner (if a plan is selected) */}
      {selectedPlan && <div className={`p-4 rounded-xl mb-4 ${selectedPlan === 'basic' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800' : selectedPlan === 'pro' ? 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800' : 'bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border border-green-200 dark:border-green-800'}`} data-id="element-4795">
          <div className="flex items-center justify-between" data-id="element-4796">
            <div className="flex items-center" data-id="element-4797">
              <Star className={`w-5 h-5 mr-2 ${selectedPlan === 'basic' ? 'text-blue-500' : selectedPlan === 'pro' ? 'text-purple-500' : 'text-green-500'}`} data-id="element-4798" />
              <div data-id="element-4799">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-4800">
                  {selectedPlan === 'basic' ? 'Basic AI Plan' : selectedPlan === 'pro' ? 'Hybrid Pro Plan' : 'Social Specialist Plan'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4801">
                  {selectedPlan === 'basic' ? 'Free - 15% AI Commission' : selectedPlan === 'pro' ? '$29.99/month - 10% AI Commission' : '$99.99/month - 8.5% AI Commission'}
                </p>
              </div>
            </div>
            <button onClick={() => setShowUpsellPopup(true)} className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4802">
              Change Plan
            </button>
          </div>
        </div>}

      {/* Earnings Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4803">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4804">
          <div className="flex justify-between items-start" data-id="element-4805">
            <div data-id="element-4806">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4807">
                Creator Rewards
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4808">$2,345</h3>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-4809">
              <DollarSign className="w-5 h-5 text-purple-500 dark:text-purple-400" data-id="element-4810" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4811">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4812" />
            <span className="text-green-500 font-medium" data-id="element-4813">15.3%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4814">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4815">
          <div className="flex justify-between items-start" data-id="element-4816">
            <div data-id="element-4817">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4818">
                Live Gifts
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4819">$1,890</h3>
            </div>
            <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg" data-id="element-4820">
              <Gift className="w-5 h-5 text-pink-500 dark:text-pink-400" data-id="element-4821" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4822">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4823" />
            <span className="text-green-500 font-medium" data-id="element-4824">8.7%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4825">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4826">
          <div className="flex justify-between items-start" data-id="element-4827">
            <div data-id="element-4828">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4829">
                Subscriptions
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4830">$3,450</h3>
            </div>
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-4831">
              <Users className="w-5 h-5 text-blue-500 dark:text-blue-400" data-id="element-4832" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4833">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4834" />
            <span className="text-green-500 font-medium" data-id="element-4835">12.1%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4836">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4837">
          <div className="flex justify-between items-start" data-id="element-4838">
            <div data-id="element-4839">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4840">
                Shop Sales
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4841">$950</h3>
            </div>
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg" data-id="element-4842">
              <ShoppingBag className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-4843" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4844">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4845" />
            <span className="text-green-500 font-medium" data-id="element-4846">5.2%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4847">
              vs last month
            </span>
          </div>
        </div>
      </div>

      {/* Metric Cards for Revenue Split and AI Commission */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4848">
        <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20 p-4 rounded-xl shadow-sm" data-id="element-4849">
          <div className="flex justify-between items-start" data-id="element-4850">
            <div data-id="element-4851">
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center group relative" data-id="element-4852">
                Revenue Split (AI vs Human)
                <Info size={14} className="ml-1 text-gray-400 cursor-help" data-id="element-4853" />
                <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-white dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-4854">
                  AI revenue is generated via automated chat, upsells, and
                  campaigns. Human revenue is generated by creators or
                  moderators.
                </span>
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4855">
                {aiPercentage}% / {humanPercentage}%
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4856">
                ${aiRevenue.toLocaleString()} / ${humanRevenue.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-4857">
              <Zap className="w-5 h-5 text-purple-500 dark:text-purple-400" data-id="element-4858" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4859">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4860" />
            <span className="text-green-500 font-medium" data-id="element-4861">11.2%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4862">
              AI growth vs last month
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 p-4 rounded-xl shadow-sm" data-id="element-4863">
          <div className="flex justify-between items-start" data-id="element-4864">
            <div data-id="element-4865">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4866">
                AI Commission Fee
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4867">
                ${aiCommission.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4868">
                {selectedPlan === 'specialist' ? '8.5%' : selectedPlan === 'pro' ? '10%' : '15%'}{' '}
                of AI-generated revenue
              </p>
            </div>
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg" data-id="element-4869">
              <DollarSign className="w-5 h-5 text-indigo-500 dark:text-indigo-400" data-id="element-4870" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4871">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4872" />
            <span className="text-green-500 font-medium" data-id="element-4873">9.5%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4874">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20 p-4 rounded-xl shadow-sm" data-id="element-4875">
          <div className="flex justify-between items-start" data-id="element-4876">
            <div data-id="element-4877">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4878">
                Avg. Completion Rate
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-4879">68.4%</h3>
            </div>
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg" data-id="element-4880">
              <Zap className="w-5 h-5 text-amber-500 dark:text-amber-400" data-id="element-4881" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-4882">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-4883" />
            <span className="text-green-500 font-medium" data-id="element-4884">5.2%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-4885">
              vs last month
            </span>
          </div>
        </div>
      </div>

      {/* Hybrid Routing Note */}
      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800 p-4" data-id="element-4886">
        <div className="flex items-start" data-id="element-4887">
          <Zap className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-4888" />
          <div data-id="element-4889">
            <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-4890">
              Hybrid Routing Logic
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-4891">
              AI handles all new and low-value customers. After $120 lifetime
              spend, customer is routed to Human operator for personalized
              attention. This threshold is configurable per Angel.
            </p>
          </div>
        </div>
      </div>

      {/* Revenue Split Panel */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4892">
        <div className="flex justify-between items-center mb-4" data-id="element-4893">
          <h3 className="text-lg font-semibold" data-id="element-4894">Revenue Split (AI vs Human)</h3>
          <button onClick={() => toggleSection('revenueSplit')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-4895">
            {expandedSections.revenueSplit ? <ChevronUp size={18} data-id="element-4896" /> : <ChevronDown size={18} data-id="element-4897" />}
          </button>
        </div>
        {expandedSections.revenueSplit && <div className="space-y-4" data-id="element-4898">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4899">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4900">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4901">
                  Total Revenue
                </h4>
                <div className="mt-2 flex items-center" data-id="element-4902">
                  <DollarSign className="w-5 h-5 text-green-500 mr-1" data-id="element-4903" />
                  <span className="text-2xl font-bold" data-id="element-4904">
                    ${totalRevenue.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg" data-id="element-4905">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4906">
                  Revenue by AI
                </h4>
                <div className="mt-2 flex items-center" data-id="element-4907">
                  <Bot className="w-5 h-5 text-indigo-500 mr-1" data-id="element-4908" />
                  <span className="text-2xl font-bold" data-id="element-4909">
                    ${aiRevenue.toLocaleString()}
                  </span>
                  <span className="ml-2 text-sm text-indigo-500" data-id="element-4910">
                    {aiPercentage}%
                  </span>
                </div>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg" data-id="element-4911">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4912">
                  Revenue by Human
                </h4>
                <div className="mt-2 flex items-center" data-id="element-4913">
                  <User className="w-5 h-5 text-green-500 mr-1" data-id="element-4914" />
                  <span className="text-2xl font-bold" data-id="element-4915">
                    ${humanRevenue.toLocaleString()}
                  </span>
                  <span className="ml-2 text-sm text-green-500" data-id="element-4916">
                    {humanPercentage}%
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4917">
              <div data-id="element-4918">
                <h4 className="text-md font-medium mb-3" data-id="element-4919">
                  Revenue Split Visualization
                </h4>
                <div className="h-64" data-id="element-4920">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-4921">
                    <PieChart data-id="element-4922">
                      <Pie data={revenueSplitData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                    name,
                    percent
                  }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-4923">
                        {revenueSplitData.map((entry, index) => <Cell key={`cell-${index}`} fill={REVENUE_SPLIT_COLORS[index % REVENUE_SPLIT_COLORS.length]} data-id="element-4924" />)}
                      </Pie>
                      <Tooltip data-id="element-4925" />
                      <Legend data-id="element-4926" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div data-id="element-4927">
                <h4 className="text-md font-medium mb-3" data-id="element-4928">AI Commission Fee</h4>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg mb-4" data-id="element-4929">
                  <div className="flex justify-between items-center mb-2" data-id="element-4930">
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4931">
                      AI Revenue
                    </span>
                    <span className="font-medium" data-id="element-4932">
                      ${aiRevenue.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2" data-id="element-4933">
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4934">
                      Commission Rate
                    </span>
                    <span className="font-medium" data-id="element-4935">
                      {aiCommissionRate * 100}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600" data-id="element-4936">
                    <span className="font-medium" data-id="element-4937">AI Commission Fee</span>
                    <span className="font-bold text-purple-600 dark:text-purple-400" data-id="element-4938">
                      ${aiCommission.toLocaleString()}
                    </span>
                  </div>
                </div>
                <h4 className="text-md font-medium mb-2" data-id="element-4939">Commission Tiers</h4>
                <div className="space-y-2" data-id="element-4940">
                  {commissionTiers.map((tier, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4941">
                      <div className="flex items-center" data-id="element-4942">
                        <div className={`w-2 h-2 rounded-full mr-2 ${index === 0 ? 'bg-red-500' : index === 1 ? 'bg-yellow-500' : 'bg-green-500'}`} data-id="element-4943"></div>
                        <span className="text-sm font-medium" data-id="element-4944">{tier.tier}</span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-4945">
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded" data-id="element-4946">
                          {tier.threshold}+
                        </span>
                        <span className="text-sm font-bold" data-id="element-4947">{tier.fee}</span>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Hybrid Customer Routing */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4948">
        <div className="flex justify-between items-center mb-4" data-id="element-4949">
          <h3 className="text-lg font-semibold" data-id="element-4950">Hybrid Customer Routing</h3>
          <button onClick={() => toggleSection('customerRouting')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-4951">
            {expandedSections.customerRouting ? <ChevronUp size={18} data-id="element-4952" /> : <ChevronDown size={18} data-id="element-4953" />}
          </button>
        </div>
        {expandedSections.customerRouting && <div className="space-y-4" data-id="element-4954">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4955">
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg" data-id="element-4956">
                <div className="flex justify-between items-start" data-id="element-4957">
                  <div data-id="element-4958">
                    <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4959">
                      AI-Handled Customers
                    </h4>
                    <div className="mt-2 flex items-center" data-id="element-4960">
                      <Bot className="w-5 h-5 text-indigo-500 mr-1" data-id="element-4961" />
                      <span className="text-2xl font-bold" data-id="element-4962">
                        {aiHandledCustomers.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm px-2 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full" data-id="element-4963">
                    {aiHandledPercentage}%
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-4964">
                  Handling new and low-value customers automatically
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-4965">
                <div className="flex justify-between items-start" data-id="element-4966">
                  <div data-id="element-4967">
                    <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4968">
                      Human-Handled Customers
                    </h4>
                    <div className="mt-2 flex items-center" data-id="element-4969">
                      <User className="w-5 h-5 text-green-500 mr-1" data-id="element-4970" />
                      <span className="text-2xl font-bold" data-id="element-4971">
                        {totalCustomers - aiHandledCustomers}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full" data-id="element-4972">
                    {100 - aiHandledPercentage}%
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-4973">
                  Focus on VIPs and high-value customers
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4974">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4975">
                  Auto-Route Threshold
                </h4>
                <div className="mt-2 flex items-center" data-id="element-4976">
                  <Settings className="w-5 h-5 text-gray-500 mr-1" data-id="element-4977" />
                  <span className="text-2xl font-bold" data-id="element-4978">$120</span>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-4979">
                  Auto-route to human after customer spends this amount
                </p>
                <button className="mt-2 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 rounded-md" data-id="element-4980">
                  Configure
                </button>
              </div>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800" data-id="element-4981">
              <div className="flex items-start" data-id="element-4982">
                <Zap className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-4983" />
                <div data-id="element-4984">
                  <h4 className="font-medium" data-id="element-4985">Efficiency Boost</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-4986">
                    AI is handling {aiHandledPercentage}% of customers, saving
                    approximately {Math.round(aiHandledPercentage * 0.8)}% of
                    operator time while maintaining revenue growth.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex justify-end" data-id="element-4987">
                <button className="px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium" data-id="element-4988">
                  Optimize Routing
                </button>
              </div>
            </div>
          </div>}
      </div>

      {/* Customer Classification Table with AI/Human Revenue columns */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-4989">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-4990">
          <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-4991">
            Customer Classification
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-4992">
          <table className="w-full" data-id="element-4993">
            <thead data-id="element-4994">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-4995">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4996">
                  Segment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4997">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4998">
                  ARPU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4999">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5000">
                  AI Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5001">
                  Human Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5002">
                  AI Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5003">
                  Routing
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5004">
              {customerSegments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-5005">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5006">
                    <div className="flex items-center" data-id="element-5007">
                      <div className="flex-shrink-0" data-id="element-5008">
                        <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-5009">
                          {segment.icon}
                        </div>
                      </div>
                      <div className="ml-3" data-id="element-5010">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5011">
                          {segment.name}
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-5012">
                            {segment.customerClass}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5013">
                          {segment.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-5014">
                    {segment.count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-5015">
                    ${segment.arpu.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-5016">
                    ${segment.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-5017">
                    <div className="flex items-center" data-id="element-5018">
                      <Bot className="w-4 h-4 text-indigo-500 mr-1" data-id="element-5019" />
                      <span data-id="element-5020">${segment.aiRevenue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-5021">
                    <div className="flex items-center" data-id="element-5022">
                      <User className="w-4 h-4 text-green-500 mr-1" data-id="element-5023" />
                      <span data-id="element-5024">${segment.humanRevenue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-5025">
                    $
                    {(segment.aiRevenue * (selectedPlan === 'specialist' ? 0.085 : selectedPlan === 'pro' ? 0.1 : 0.15)).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5026">
                    <span className={`px-2 py-1 text-xs rounded-full ${segment.aiHandled > segment.humanHandled ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'}`} data-id="element-5027">
                      {segment.aiHandled > segment.humanHandled ? 'AI-handled' : 'Human-handled'}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export button with enhanced functionality */}
      <div className="flex justify-end mb-6" data-id="element-5028">
        <button onClick={handleExportData} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium" data-id="element-5029">
          <Download size={16} data-id="element-5030" />
          <span data-id="element-5031">Export Revenue & Commission Report</span>
        </button>
      </div>

      {/* Commission & Payroll Automation */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-5032">
        <div className="flex justify-between items-center mb-4" data-id="element-5033">
          <h3 className="text-lg font-semibold" data-id="element-5034">
            Commission & Payroll Automation
          </h3>
          <button onClick={() => toggleSection('commissionAutomation')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5035">
            {expandedSections.commissionAutomation ? <ChevronUp size={18} data-id="element-5036" /> : <ChevronDown size={18} data-id="element-5037" />}
          </button>
        </div>
        {expandedSections.commissionAutomation && <div className="space-y-4" data-id="element-5038">
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800" data-id="element-5039">
              <div className="flex items-start" data-id="element-5040">
                <DollarSign className="w-5 h-5 text-purple-500 mr-2 mt-0.5" data-id="element-5041" />
                <div data-id="element-5042">
                  <h4 className="font-medium" data-id="element-5043">Commission Summary</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-5044">
                    AI earned you ${aiRevenue.toLocaleString()} (commission $
                    {aiCommission.toLocaleString()}), Human operators earned $
                    {humanRevenue.toLocaleString()}.
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5045">
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg" data-id="element-5046">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5047">
                  AI Commission
                </h4>
                <div className="mt-2 flex items-center" data-id="element-5048">
                  <Bot className="w-5 h-5 text-indigo-500 mr-1" data-id="element-5049" />
                  <span className="text-2xl font-bold" data-id="element-5050">
                    ${aiCommission.toLocaleString()}
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-5051">
                  Platform fee for AI-driven revenue
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-5052">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5053">
                  Human Operator Commissions
                </h4>
                <div className="mt-2 flex items-center" data-id="element-5054">
                  <User className="w-5 h-5 text-green-500 mr-1" data-id="element-5055" />
                  <span className="text-2xl font-bold" data-id="element-5056">
                    ${(humanRevenue * 0.3).toLocaleString()}
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-5057">
                  Total commissions paid to human operators
                </p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-5058">
                <h4 className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5059">
                  Your Net Earnings
                </h4>
                <div className="mt-2 flex items-center" data-id="element-5060">
                  <DollarSign className="w-5 h-5 text-blue-500 mr-1" data-id="element-5061" />
                  <span className="text-2xl font-bold" data-id="element-5062">
                    $
                    {(totalRevenue - aiCommission - humanRevenue * 0.3).toLocaleString()}
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-5063">
                  After all commissions and fees
                </p>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-5064">
              <button onClick={handleExportData} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium" data-id="element-5065">
                <Download size={16} data-id="element-5066" />
                <span data-id="element-5067">Export Commission Report</span>
              </button>
            </div>
          </div>}
      </div>

      {/* Operator Performance Snapshot */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-5068">
        <div className="flex justify-between items-center mb-4" data-id="element-5069">
          <h3 className="text-lg font-semibold" data-id="element-5070">Operator Performance</h3>
          <button onClick={() => toggleSection('operatorPerformance')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5071">
            {expandedSections.operatorPerformance ? <ChevronUp size={18} data-id="element-5072" /> : <ChevronDown size={18} data-id="element-5073" />}
          </button>
        </div>
        {expandedSections.operatorPerformance && <div className="space-y-4" data-id="element-5074">
            <div className="overflow-x-auto" data-id="element-5075">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5076">
                <thead data-id="element-5077">
                  <tr data-id="element-5078">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5079">
                      Operator
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5080">
                      Revenue
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5081">
                      Customers
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5082">
                      Commission
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5083">
                      ARPU
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5084">
                  {operatorPerformance.map(operator => <tr key={operator.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${operator.name === 'AI Assistant' ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`} data-id="element-5085">
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-5086">
                        <div className="flex items-center" data-id="element-5087">
                          {operator.name === 'AI Assistant' ? <Bot className="w-4 h-4 text-indigo-500 mr-2" data-id="element-5088" /> : <User className="w-4 h-4 text-green-500 mr-2" data-id="element-5089" />}
                          <span className="font-medium" data-id="element-5090">{operator.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-5091">
                        ${operator.revenue.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-5092">
                        {operator.customers.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-5093">
                        ${operator.commission.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-5094">
                        ${(operator.revenue / operator.customers).toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800" data-id="element-5095">
              <div className="flex items-start" data-id="element-5096">
                <Zap className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-5097" />
                <div data-id="element-5098">
                  <h4 className="font-medium" data-id="element-5099">Efficiency Metric</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-5100">
                    AI saved {aiHandledPercentage}% of chatter time by handling
                    low-value customers, allowing human operators to focus on
                    high-value relationships.
                  </p>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Campaign Hub Expansion */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-5101">
        <div className="flex justify-between items-center mb-4" data-id="element-5102">
          <h3 className="text-lg font-semibold" data-id="element-5103">Campaign Hub</h3>
          <button onClick={() => toggleSection('campaignHub')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5104">
            {expandedSections.campaignHub ? <ChevronUp size={18} data-id="element-5105" /> : <ChevronDown size={18} data-id="element-5106" />}
          </button>
        </div>
        {expandedSections.campaignHub && <div className="space-y-4" data-id="element-5107">
            <ul className="space-y-3" data-id="element-5108">
              {campaignData.map(campaign => <li key={campaign.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-5109">
                  <div className="flex items-center" data-id="element-5110">
                    <div className={`p-2 ${campaign.type === 'AI-driven' ? 'bg-indigo-100 dark:bg-indigo-900' : 'bg-green-100 dark:bg-green-900'} rounded-lg mr-3`} data-id="element-5111">
                      {campaign.type === 'AI-driven' ? <Bot className={`w-5 h-5 ${campaign.type === 'AI-driven' ? 'text-indigo-500 dark:text-indigo-400' : 'text-green-500 dark:text-green-400'}`} data-id="element-5112" /> : <User className={`w-5 h-5 ${campaign.type === 'AI-driven' ? 'text-indigo-500 dark:text-indigo-400' : 'text-green-500 dark:text-green-400'}`} data-id="element-5113" />}
                    </div>
                    <div data-id="element-5114">
                      <h4 className="font-medium" data-id="element-5115">{campaign.name}</h4>
                      <div className="flex items-center" data-id="element-5116">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5117">
                          {campaign.status}
                        </p>
                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${campaign.type === 'AI-driven' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300' : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'}`} data-id="element-5118">
                          {campaign.type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="text-green-500 font-medium" data-id="element-5119">
                    ${campaign.earned} earned
                  </span>
                </li>)}
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5120">
              <button className="py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-medium flex items-center justify-center gap-2" data-id="element-5121">
                <Bot size={16} data-id="element-5122" />
                <span data-id="element-5123">Create AI Campaign</span>
              </button>
              <button className="py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg font-medium flex items-center justify-center gap-2" data-id="element-5124">
                <User size={16} data-id="element-5125" />
                <span data-id="element-5126">Create Human Campaign</span>
              </button>
            </div>
          </div>}
      </div>

      {/* Historical Revenue Graph */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-5127">
        <h3 className="text-lg font-semibold mb-4" data-id="element-5128">Historical Revenue</h3>
        <div className="h-80" data-id="element-5129">
          <ResponsiveContainer width="100%" height="100%" data-id="element-5130">
            <AreaChart data={monthlyEarnings} data-id="element-5131">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" data-id="element-5132" />
              <XAxis dataKey="name" stroke="#6B7280" data-id="element-5133" />
              <YAxis stroke="#6B7280" data-id="element-5134" />
              <Tooltip contentStyle={{
              backgroundColor: '#1F2937',
              borderColor: '#374151',
              color: '#F9FAFB'
            }} data-id="element-5135" />
              <Area type="monotone" dataKey="creator" stackId="1" stroke="#8884d8" fill="#8884d8" data-id="element-5136" />
              <Area type="monotone" dataKey="gifts" stackId="1" stroke="#82ca9d" fill="#82ca9d" data-id="element-5137" />
              <Area type="monotone" dataKey="subscriptions" stackId="1" stroke="#ffc658" fill="#ffc658" data-id="element-5138" />
              <Area type="monotone" dataKey="shop" stackId="1" stroke="#ff7300" fill="#ff7300" data-id="element-5139" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-5140">
        <h3 className="text-lg font-semibold mb-4" data-id="element-5141">Recent Activity</h3>
        <div className="space-y-3" data-id="element-5142">
          {recentActivity.map(activity => <div key={activity.id} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-5143">
              <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-3" data-id="element-5144">
                {activity.icon}
              </div>
              <div className="flex-1" data-id="element-5145">
                <div className="flex justify-between" data-id="element-5146">
                  <h4 className="font-medium" data-id="element-5147">{activity.customer}</h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5148">
                    {activity.time}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5149">
                  {activity.action}
                </p>
                {activity.amount > 0 && <span className="text-green-500 font-medium" data-id="element-5150">
                    ${activity.amount}
                  </span>}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}